import {
  Box,
  CircularProgress,
  ConfirmationDialogBox,
  FormLabel,
  Grid,
} from '@atgfr8/atg-ui-tailwind';
import { DeleteIcon, InformationIcon } from '@atgfr8/icons';
import { CategoryT } from '@common/types';
import { LinkComponent, ToastType } from '@components';
import { v4ServeDocumentUrl } from '@constants/urls';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { createError } from '@utils/errorUtils';
import { handleToast, isAdmin, printLog } from '@utils/utils';
import { differenceWith } from 'lodash';
import { createEffect, createSignal, onCleanup, onMount, Show } from 'solid-js';

import { HoverPop } from '../HoverPop/HoverPop';
import { classes } from './FileUpload.style';
import { DeleteFile, UploadDocument } from './services';
import {
  DocumentEntityType,
  DocumentModel,
  DocumentUploadResponse,
} from './types';

type Props = {
  height?: number;
  width?: number;
  label?: string;
  disabled?: boolean;
  alreadyUploadedDocuments?: DocumentModel[];
  documentEntityType: DocumentEntityType;
  entityId: number;
  onChange?: (files: fileUpload[]) => void;
  required?: boolean;
  acceptedFileTypes?:
    | 'pdf'
    | 'jpeg'
    | 'jpg'
    | 'png'
    | 'doc'
    | 'docx'
    | 'all'
    | 'url'
    | string;
  showAttachFiles?: boolean;
  categories?: CategoryT[];
  changeTrackingSource?: ChangeTrackingSource;
  deleteChangeTrackingSource?: ChangeTrackingSource;
  uploadChangeTrackingSource?: ChangeTrackingSource;
};

type fileUpload = {
  file?: File | null;
  document: DocumentModel;
};

const DEFAULT_HEIGHT = 150;
const DEFAULT_WIDTH = 100;
const DEFAULT_LABEL = 'Click or Drag a File To Upload';

// eslint-disable-next-line complexity
function FileUpload(props: Readonly<Props>) {
  const [files, setFiles] = createSignal<fileUpload[]>(
    props.alreadyUploadedDocuments?.map((x) => {
      return {
        document: x,
      } as fileUpload;
    }) ?? [],
  );
  const [fileToDelete, setFileToDelete] = createSignal<fileUpload | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [inputRef, setInputRef] = createSignal<HTMLInputElement>();
  const [dropZoneRef, setDropZoneRef] = createSignal<HTMLDivElement>();
  const [getIsDialogOpen, setIsDialogOpen] = createSignal(false);
  const renderedItems: fileUpload[] | null = [];
  const reconcileFileListToApiUploadResponse = (
    uploadResponse: DocumentUploadResponse,
  ): fileUpload[] => {
    const uploads: fileUpload[] = [];
    uploadResponse.results.forEach((res) => {
      uploads.push({
        document: {
          name: res.name,
          url: res.url,
        },
      });
    });
    return uploads;
  };

  createEffect(() => {
    const diff = differenceWith(
      props.alreadyUploadedDocuments ?? [],
      files(),
      (a, b) => a.name === b.document.name,
    );

    if (diff.length > 0) {
      setFiles((prev) => {
        const newFiles = diff.map((doc) => {
          return {
            document: doc,
          } as fileUpload;
        });

        return [...prev, ...newFiles];
      });
    }
  });

  // Event handler for file change
  const handleFileChange = async (event: Event, bypassState = false) => {
    const target = event.target as HTMLInputElement;
    const selectedFiles = target.files;
    const acceptedFileTypes =
      props.acceptedFileTypes === undefined ? 'all' : props.acceptedFileTypes;

    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    const dt = new DataTransfer();
    const filesThatExceedLimit: string[] = [];

    for (const file of selectedFiles) {
      if (file.size > 5_000_000) {
        filesThatExceedLimit.push(file.name);
      } else {
        dt.items.add(file);
      }
    }

    if (filesThatExceedLimit.length > 0) {
      handleToast(
        ToastType.Error,
        `The following files exceed the size limit of 5Mb and will be excluded from the upload: ${filesThatExceedLimit.join(
          ',',
        )}`,
      );
    }

    if (dt.files.length === 0) {
      return;
    }

    if (acceptedFileTypes) {
      const acceptedTypes = acceptedFileTypes.split(',');
      for (const file of dt.files) {
        if (
          !acceptedTypes.some((type) => {
            return file.type.includes(type.trim()) || type === 'all';
          })
        ) {
          target.value = '';
          return;
        }
      }
    }

    if (!bypassState) {
      setLoading(true);
    }

    try {
      const response = await UploadDocument(
        {
          documentEntityType: props.documentEntityType,
          entityId: props.entityId,
          file: dt.files,
        },
        props.uploadChangeTrackingSource ?? props.changeTrackingSource,
      );
      const newFiles = reconcileFileListToApiUploadResponse(response);

      setFiles((prevFiles) => {
        const allFiles = [...prevFiles, ...newFiles];
        props.onChange?.(allFiles);
        return allFiles;
      });
    } catch (error) {
      const err = createError(error, `Failed to upload ${dt.files[0].name}`);
      handleToast(ToastType.Error, err.message);
    }

    if (!bypassState) {
      setLoading(false);
    }
  };

  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    const dropZone = dropZoneRef();

    if (dropZone) {
      dropZone.style.backgroundColor = '#d3ebf0';
      dropZone.style.border = '3px solid #026ea1';
    }
  };

  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    const dropZone = dropZoneRef();

    if (dropZone) {
      dropZone.style.backgroundColor = 'rgb(242, 246, 248)';
      dropZone.style.border = '3px solid transparent';
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const dropZone = dropZoneRef();

    if (dropZone) {
      dropZone.style.backgroundColor = 'rgb(242, 246, 248)';
      dropZone.style.border = '3px solid transparent';
    }

    const fileList: File[] = [];
    const promises: Promise<void>[] = [];
    let unsupportedFileType = false;

    setLoading(true);

    [...(e.dataTransfer?.items || [])].forEach((item) => {
      if (item.type === 'text/x-moz-url' || item.type === 'text/uri-list') {
        unsupportedFileType = true;
      } else if (item.kind === 'file') {
        const file = item.getAsFile();

        if (file !== null) {
          fileList.push(file);

          const event = {
            target: {
              files: [file],
            },
          } as unknown as Event;

          promises.push(handleFileChange(event, true));
        }
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (unsupportedFileType) {
      handleToast(
        ToastType.Error,
        'The file you dropped cannot be uploaded. Please download it to your computer and try uploading again',
      );
    }

    void Promise.all(promises)
      .then(() => {
        setLoading(false);
      })
      .catch((reason) => {
        printLog(reason);
      });
  };

  onMount(() => {
    window.onbeforeunload = () => {
      if (loading()) {
        return 'There are pending file uploads. Are you sure you want to leave/refresh?';
      }
    };

    const dropZone = dropZoneRef();

    if (dropZone) {
      dropZone.addEventListener('dragover', onDragOver);
      dropZone.addEventListener('dragenter', onDragOver);
      dropZone.addEventListener('dragleave', onDragLeave);
      dropZone.addEventListener('drop', onDrop);
      dropZone.style.transition = '0.2s ease-in-out';
      dropZone.style.border = '3px solid transparent';
      dropZone.style.borderRadius = '6px';
    }
  });

  onCleanup(() => {
    dropZoneRef()?.removeEventListener('dragover', onDragOver);
    dropZoneRef()?.removeEventListener('dragenter', onDragOver);
    dropZoneRef()?.removeEventListener('dragleave', onDragLeave);
    dropZoneRef()?.removeEventListener('drop', onDrop);
  });

  const requestFileDeletion = (file: fileUpload) => {
    const index = renderedItems.findIndex(
      (x) => x.document.name === file.document.name,
    );
    if (index > -1) {
      renderedItems.splice(index, 1);
      setFileToDelete(file);
      setIsDialogOpen(true);
    }
  };

  const confirmFileDeletion = async () => {
    if (fileToDelete()) {
      const url = fileToDelete()?.document.url;
      if (url != null) {
        setLoading(true);

        try {
          const response = await DeleteFile(
            url,
            props.deleteChangeTrackingSource ?? props.changeTrackingSource,
          );

          if (response) {
            setFiles((prevFiles) => {
              const newFiles = prevFiles.filter(
                (file) => file !== fileToDelete(),
              );
              setFileToDelete(null);
              props.onChange?.(newFiles);
              return newFiles;
            });
            setIsDialogOpen(false);
          }
        } catch (error) {
          handleToast(ToastType.Error, (error as Error).message);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const cancelFileDeletion = () => {
    if (!renderedItems.includes(fileToDelete() as fileUpload)) {
      renderedItems.push(fileToDelete() as fileUpload);
    }
    setFileToDelete(null);
    setIsDialogOpen(false);
  };

  const renderFileList = (
    <Grid>
      {files().map((file) => {
        if (!renderedItems.includes(file)) {
          renderedItems.push(file);
        }

        return (
          <Grid container>
            <Grid size={12}>
              <div class="flex ml-2 gap-x-2 items-center">
                <LinkComponent
                  target={'_blank'}
                  url={`${v4ServeDocumentUrl.doc(
                    encodeURIComponent(
                      file.file ? file.file.name : file.document.url,
                    ),
                  )}`}
                  title={file.file ? file.file.name : file.document.name}
                />
                {!loading() && !renderedItems.includes(file) && file.file ? (
                  <Box style={classes.LoadingTextBoxStyles()}>
                    {file.file.size / 1000}kb • Loading
                  </Box>
                ) : file.file ? (
                  <Box style={classes.LoadingTextBoxStyles()}>
                    {file.file.size / 1000}kb
                  </Box>
                ) : (
                  <></>
                )}
                <Show when={isAdmin() && !loading()}>
                  <DeleteIcon
                    class="cursor-pointer self-end mt-[5px]"
                    color="#B00020"
                    onClick={() => requestFileDeletion(file)}
                  />
                </Show>
              </div>
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );

  const handleSubmit = async () => {
    await confirmFileDeletion();
  };

  return (
    <>
      <div
        class="w-full text-center flex flex-col relative"
        ref={setDropZoneRef}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <Grid
          container
          style={{
            ...classes.iconLabelContainer(props.height ?? DEFAULT_HEIGHT),
            cursor: 'pointer',
            border: '1px dashed lightgrey',
            'border-radius': '5px',
          }}
          class="[&>div>fieldset]:border-none"
          onClick={() => inputRef()?.click()}
          height="40px"
          width="100%"
        >
          <FormLabel
            style={{
              color: (props.disabled ?? false) || loading() ? 'lightgrey' : '',
            }}
          >
            {loading() ? (
              <>Files are uploading please wait</>
            ) : (
              (props.label ?? (
                <div class="flex items-center cursor-pointer">
                  <div>{DEFAULT_LABEL}</div>
                  <div class="relative z-10 ml-2">
                    <HoverPop anchor={<InformationIcon />} render={true}>
                      <div class="p-2">
                        SVG, PNG, JPG, GIF, CSV, TIFF, BMP, or, PDF (max. 5mb)
                      </div>
                    </HoverPop>
                  </div>
                </div>
              ))
            )}
          </FormLabel>
        </Grid>
        <input
          {...props}
          type="file"
          multiple
          ref={setInputRef}
          style={{
            height: `${props.height ?? DEFAULT_HEIGHT}px`,
            width: `${props.width ?? DEFAULT_WIDTH}%`,
          }}
          class="invisibile opacity-0 cursor-pointer z-2"
          disabled={(props.disabled ?? false) || loading()}
          onChange={handleFileChange}
          required={props.required}
        />
        {(props.showAttachFiles ?? true) && files().length > 0 && (
          <>{renderFileList}</>
        )}
        <Show when={loading()}>
          <Box style={classes.BoxStyles()}>
            <Box>
              <CircularProgress size={50} />
            </Box>
          </Box>
        </Show>
      </div>
      <ConfirmationDialogBox
        dataTestId="deleteUploadedDocument"
        id="deleteUploadedDocument"
        title="Are you sure you want to delete this document?"
        onSubmit={handleSubmit}
        onSubmitText="Delete"
        onCancel={cancelFileDeletion}
        isDialogOpen={getIsDialogOpen()}
        closeDialogBox={(_reason) => setIsDialogOpen(false)}
      />
    </>
  );
}

export default FileUpload;
